import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  UseDataGridConfig,
  useDataGridMasterDetailConfig,
} from '~/components/DataGridCustom';

import { PurchaseOrderDetailsDataGridField, PurchaseOrderDetailsDataGridRow } from './PurchaseOrderDetails.types';

export const usePurchaseOderDetailsDataGridConfig: UseDataGridConfig<PurchaseOrderDetailsDataGridRow> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'PurchaseOrderDetailsWidget.table' });

  const { defaultToggleColumnDefinition, ...masterDetailProps } =
    useDataGridMasterDetailConfig<PurchaseOrderDetailsDataGridRow>();

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    return {
      sortModel: [],
      columnVisibilityModel: {},
      columns: [
        // required to include toggle column definition to preserve
        // column order on remote state reset invocation
        defaultToggleColumnDefinition,
        createColumn({
          field: PurchaseOrderDetailsDataGridField.POSITION,
          minWidth: 60,
          hideable: false,
          sortable: false,
        }),
        createColumn({
          field: PurchaseOrderDetailsDataGridField.TITLE,
          renderCell: cellFactory(EmptyFallbackCell),
          minWidth: 50,
          flex: 1,
          sortable: false,
        }),
        createColumn({
          field: PurchaseOrderDetailsDataGridField.QUANTITY,
          renderCell: cellFactory(EmptyFallbackCell),
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
        createColumn({
          field: PurchaseOrderDetailsDataGridField.UNIT,
          renderCell: cellFactory(CuriosityValue, ({ value }) => ({
            ns: CuriosityNamespaceType.UNIT,
            i18nKey: value,
            fallback: NULLISH_VALUE,
          })),
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
        createColumn({
          field: PurchaseOrderDetailsDataGridField.PRICE_PER_UNIT,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency })),
          minWidth: 200,
          align: GridAlignmentType.RIGHT,
          sortable: false,
        }),
      ],
      ...masterDetailProps,
    } as DataGridConfig<PurchaseOrderDetailsDataGridRow>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tColumn, t, defaultToggleColumnDefinition]);
};
